/**
 * SMS Opt-In Prompt
 *
 * A dismissible nudge on the crew dashboard pointing at the opt-in form in
 * Profile → Text Message Notifications.
 *
 * This exists because A2P 10DLC consent cannot be granted on a crew member's
 * behalf — every person who wants texts has to walk to the settings page and
 * opt in themselves. Without a prompt, essentially nobody would, and the SMS
 * feature would sit unused. It is the migration path for existing crew and the
 * onboarding path for new ones.
 *
 * Rules it follows deliberately:
 *  - Never shown to someone who already opted in.
 *  - Never shown to someone who opted OUT. Re-asking a person who said no is
 *    the behavior that gets a brand reported, and it is rude besides.
 *  - Dismissal is remembered per user. One "not now" means not again.
 *  - It is a link, not a form. Consent is only ever captured on the profile
 *    page, where the full disclosure is displayed.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getSmsConsentStatus } from '../../api/smsConsent';

/** Per-user so a shared browser doesn't hide the prompt from the next person. */
function dismissKey(userId: string): string {
  return `programrow.smsPromptDismissed.${userId}`;
}

interface Props {
  userId: string | undefined;
}

export default function SmsOptInPrompt({ userId }: Props) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!userId) return;

    let cancelled = false;

    if (localStorage.getItem(dismissKey(userId)) === '1') return;

    getSmsConsentStatus()
      .then((status) => {
        if (cancelled) return;
        // Respect both an existing opt-in and a deliberate opt-out.
        if (status.enabled || status.optedOut || status.awaitingVerification) return;
        setVisible(true);
      })
      .catch(() => {
        // A failed status check is not a reason to nag. Stay quiet.
      });

    return () => {
      cancelled = true;
    };
  }, [userId]);

  function dismiss() {
    if (userId) localStorage.setItem(dismissKey(userId), '1');
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div className="mb-6 rounded-lg border border-hairline border-l-[3px] border-l-rack bg-surface-panel p-4">
      <div className="flex items-start gap-3">
        <svg
          className="mt-0.5 h-5 w-5 shrink-0 text-primary-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
          />
        </svg>

        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-gray-900">
            Want a text when you're assigned?
          </h3>
          <p className="mt-1 text-sm text-gray-700">
            Get a message when you're added to an event or a call time changes. Takes about
            thirty seconds to set up, and you can turn it off any time by replying STOP.
          </p>
          <div className="mt-3 flex flex-wrap gap-3">
            <Link
              to="/crew/profile#sms-notifications"
              className="inline-flex items-center rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-700"
            >
              Set up text alerts
            </Link>
            <button
              type="button"
              onClick={dismiss}
              className="inline-flex items-center rounded-md px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:text-gray-900"
            >
              Not now
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="shrink-0 rounded p-1 text-gray-400 transition-colors hover:text-gray-600"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}
